<template>
  <SearchBarSharedComponent
    v-if="localSearchModel"
    :key="searchModel?.radius ?? 'no-radius'"
    :theme="theme"
    :translations="translations"
    :searchModel="localSearchModel"
    :indicateExtendedFilters="showDesktopIndicator"
    :validationState="validationState"
    :preSearchIsLoading="preSearchIsLoading"
    :selectedGeoLocations="selectedGeoLocations"
    :locale="currentLocale"
    :searchResults="autocompleteResults"
    :isDedicatedMobileLayout="true"
    :isFreeTextFieldEnabled="isTextSearchEnabled"
    @triggerAutocompleteLocations="searchLocations"
    @locationsChanged="onLocationsChanged($event)"
    @radiusChanged="radiusChanged"
    @searchRoomsChanged="setRooms($event)"
    @priceChanged="onPriceChange($event)"
    @offerTypeChanged="offerTypeChanged($event)"
    @floorTypeChanged="floorTypeChanged($event)"
    @searchSurfaceUsableChanged="surfaceUsableRangeChanged($event)"
    @surfacePropertyRangeChanged="surfacePropertyRangeChanged($event)"
    @surfaceLivingRangeChanged="surfaceLivingRangeChanged($event)"
    @surfaceUsableRangeChanged="surfaceUsableRangeChanged($event)"
    @buildYearRangeChanged="buildYearRangeChanged($event)"
    @volumeRangeChanged="volumeRangeChanged($event)"
    @requireFacilityChanged="requireFacilityChanged($event.facility, $event.isRequired)"
    @objectTypesChanged="objectTypesChanged($event)"
    @availabilityChanged="availabilityChanged($event)"
    @chooseTypeChanged="chooseTypeChanged($event)"
    @doSearch="search()"
    @resetSearch="onResetSearch()"
    @freeTextFieldChanged="freeTextFieldChanged"
    @freeTextValidationChanged="saveTextFieldValidationState"
    @extendedFiltersClick="trackExtendedFilters"
  />
</template>

<script lang="ts">
import { hgNumber, HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation } from '@smg-real-estate/geo-libs';
import {
  ChooseType,
  Facility,
  type NumberRange,
  ObjectType,
  SearchModel,
} from '@smg-real-estate/hg-search-libs';
import {
  PriceTypeOptions,
  SearchBar as SearchBarSharedComponent,
  ValidationStates,
} from '@smg-real-estate/seeker-app-shared-components';
import { throttle } from 'lodash-es';
import { defineComponent, type PropType } from 'vue';
import { mapGetters, mapMutations } from 'vuex';

import { getCurrentPlatformTheme } from '@/helpers/platformDetection';
import { getPassThroughHeaders } from '@/helpers/RequestHeaders/RequestHeaders';
import { getSSRContext } from '@/helpers/SSR/ssr';
import { getLayoutSolutionRequest } from '@/resultlist/layoutSolutionRequest';
import {
  trackOpenExtendedFilter,
  trackResetFiltersClicked,
} from '@/resultlist/LayoutSolutionResultListPage.tracking';
import { LayoutSolutionRequest } from '@/types';
import { useI18n } from 'vue-i18n';
import { formatResultCount, hasExtendedFiltersForDesktop } from './helpers';

function isEmptyRange(range: NumberRange | undefined): boolean {
  return range === undefined || (range.from === undefined && range.to === undefined);
}

function isDesktop(): boolean {
  /* Domus $viewport-sm-min value */
  const DESKTOP_MIN_WIDTH = 768;
  return window.innerWidth >= DESKTOP_MIN_WIDTH;
}

export default defineComponent({
  name: 'SearchBarWrapper',
  components: {
    SearchBarSharedComponent,
  },
  props: {
    searchModel: {
      required: true,
      type: Object as PropType<SearchModel>,
    },
    allowEmptyLocation: {
      type: Boolean,
      default: false,
    },
    isTextSearchEnabled: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const i18n = useI18n();
    return {
      ssrContext: getSSRContext(),
      i18n,
    };
  },
  data() {
    const objectTypes: Record<string, boolean> = Object.keys(ObjectType)
      .concat('ALL')
      .reduce(
        (previous: Record<string, boolean>, current: string) => {
          previous[current] = false;
          return previous;
        },
        {} as Record<string, boolean>,
      );

    return {
      theme: getCurrentPlatformTheme(),
      objectTypes,
      freeTextFieldValidationState: ValidationStates.Valid,
      priceType: PriceTypeOptions.ABSOLUTE,
      localPriceRange: {} as NumberRange | undefined,
      isSearchedWithNoLocations: false,
      isDesktop: typeof window === 'undefined' ? false : isDesktop(),
      updateViewportThrottled: () => {},
    };
  },
  computed: {
    currentLocale() {
      return this.i18n.locale.value as HgSupportedLanguage;
    },
    translations() {
      const messages = this.i18n.getLocaleMessage(this.i18n.locale.value) as {
        searchBar?: Record<string, string>;
        resultlist?: Record<string, string>;
      };

      const searchBar = messages?.searchBar ?? {};
      const resultlist = messages?.resultlist ?? {};

      return {
        labels: {
          ...searchBar,
          search: this.i18n.t('searchBar.search', {
            resultCount: this.preSearchResultCountFormatted,
          }),
          fromRooms: this.i18n.t('searchBar.fromRooms', { from: this.fromRoomsLocalized }),
          toRooms: this.i18n.t('searchBar.toRooms', { to: this.toRoomsLocalized }),
        },
        options: {
          ...resultlist,
          objectType: this.$tm('listingCategories'),
        },
      };
    },
    preSearchResultCountFormatted() {
      return formatResultCount(this.preSearchResultCount, this.i18n.locale.value);
    },
    facilities() {
      return Object.values(Facility).reduce(
        (previousResult: Record<Facility, boolean>, currentKey: Facility) => {
          previousResult[currentKey] = this.searchModel.facilitiesRequired.includes(currentKey);
          return previousResult;
        },
        {} as Record<Facility, boolean>,
      );
    },
    selectedGeoLocations(): GeoLocation[] {
      if (this.localSearchModel?.locations === undefined) {
        return [];
      }

      return (this.localSearchModel.locations
        ?.map((id: string) => this.$store.getters['geo/getGeoLocationById'](id))
        .filter((it: GeoLocation | undefined) => it !== undefined) ?? []) as GeoLocation[];
    },
    fromRoomsLocalized(): string {
      return this.localizeNumber((this.localSearchModel as SearchModel).numberOfRooms?.from);
    },
    toRoomsLocalized(): string {
      return this.localizeNumber((this.localSearchModel as SearchModel).numberOfRooms?.to);
    },
    showDesktopIndicator(): boolean {
      return hasExtendedFiltersForDesktop(this.searchModel);
    },
    noLocations(): boolean {
      return this.localSearchModel?.locations?.length === 0;
    },
    validationState(): ValidationStates {
      if (!this.allowEmptyLocation) {
        if (this.isSearchedWithNoLocations) {
          return ValidationStates.InvalidShowError;
        }
        if (this.noLocations) {
          return ValidationStates.InvalidHideError;
        }
      }

      if (this.freeTextFieldValidationState !== ValidationStates.Valid)
        return this.freeTextFieldValidationState;

      if (this.preSearchDataFetchError) return ValidationStates.InvalidDataFetchError;

      return ValidationStates.Valid;
    },
    layoutSolutionRequest(): LayoutSolutionRequest | undefined {
      return getLayoutSolutionRequest(this.$route.query);
    },
    ...mapGetters({
      localSearchModel: 'searchBar/searchModel',
      fullSearchModel: 'resultList/lastSearchModel',
      autocompleteResults: 'geo/getAutocompleteResults',
      preSearchIsLoading: 'resultList/preSearchIsLoading',
      preSearchDataFetchError: 'resultList/preSearchDataFetchError',
      preSearchResultCount: 'resultList/preSearchResultCount',
      hasFromYearlyRentPerSqm: 'searchBar/hasFromYearlyRentPerSqm',
      hasToYearlyRentPerSqm: 'searchBar/hasToYearlyRentPerSqm',
    }),
  },
  watch: {
    priceType(newValue: PriceTypeOptions) {
      if (
        newValue === PriceTypeOptions.ABSOLUTE &&
        !isEmptyRange(this.localSearchModel.yearlyRentPerSqmRange)
      ) {
        this.setLocalSearchModel(
          this.localSearchModel
            .withPriceRange(this.localSearchModel.yearlyRentPerSqmRange)
            .withYearlyRentPerSqmRange(undefined),
        );
      } else if (
        newValue === PriceTypeOptions.YEARLY_RENT_PER_SQM &&
        !isEmptyRange(this.localSearchModel.priceRange)
      ) {
        this.setLocalSearchModel(
          this.localSearchModel
            .withYearlyRentPerSqmRange(this.localSearchModel.priceRange)
            .withPriceRange(undefined),
        );
      }
    },
    localSearchModel(localSearchModel: SearchModel) {
      this.localPriceRange = isEmptyRange(localSearchModel.yearlyRentPerSqmRange)
        ? localSearchModel.priceRange
        : localSearchModel.yearlyRentPerSqmRange;

      /* Prevent presearch if:
        1) no location provided;
        2) searchbar searchModel already matches the resultList one
      */
      if (
        (!this.allowEmptyLocation && this.noLocations) ||
        localSearchModel.isEqual(this.fullSearchModel)
      ) {
        return;
      }

      Object.keys(this.objectTypes).forEach((objectType) => {
        if (objectType !== 'ALL') {
          const objectTypeKey = ObjectType[objectType as keyof typeof ObjectType];
          this.objectTypes[objectType] = localSearchModel.objectTypes.includes(objectTypeKey);
        }
      });

      this.$store.dispatch('resultList/doSearch', {
        name: 'preSearch',
        language: this.i18n.locale.value,
        searchModel: localSearchModel.withPageSize(0),
        layoutSolutionRequest: this.layoutSolutionRequest,
        ...(this.ssrContext?.passThroughHeaders && {
          passThroughHeaders: getPassThroughHeaders(this.ssrContext.passThroughHeaders),
        }),
      });
    },
    searchModel: {
      immediate: true,
      handler(searchModel: SearchModel) {
        this.setLocalSearchModel(searchModel);

        // we should also reset the preSearch result to avoid differences in the result count
        // this can happen when the router doesn't reload the page and the preSearch result is already set
        this.$store.commit('resultList/setResult', {
          name: 'preSearch',
          result: null,
          error: null,
        });

        if (this.hasToYearlyRentPerSqm || this.hasFromYearlyRentPerSqm) {
          this.priceType = PriceTypeOptions.YEARLY_RENT_PER_SQM;
          this.localPriceRange = searchModel.yearlyRentPerSqmRange;
        } else {
          this.priceType = PriceTypeOptions.ABSOLUTE;
          this.localPriceRange = searchModel.priceRange;
        }
      },
    },
    fullSearchModel: {
      handler(fullSearchModel: SearchModel | null) {
        // After a full search completes, sync the searchBar store with the executed search
        // This ensures the UI reflects the actual search criteria that was used
        if (fullSearchModel && !fullSearchModel.isEqual(this.localSearchModel)) {
          this.setLocalSearchModel(fullSearchModel);
        }
      },
    },
  },
  mounted() {
    this.updateViewport();

    this.updateViewportThrottled = throttle(this.updateViewport, 300);
    window.addEventListener('resize', this.updateViewportThrottled);
  },
  unmounted() {
    window.removeEventListener('resize', this.updateViewportThrottled);
  },
  methods: {
    updateViewport() {
      this.isDesktop = isDesktop();
    },
    requireFacilityChanged(facility: Facility, isRequired: boolean) {
      this.setLocalSearchModel(this.localSearchModel.requireFacility(facility, isRequired));
    },
    localizeNumber(value?: number): string {
      return value === undefined
        ? ''
        : hgNumber(value, this.i18n.locale.value as HgSupportedLanguage);
    },
    search() {
      if (this.freeTextFieldValidationState !== ValidationStates.Valid) {
        return;
      }

      if (!this.allowEmptyLocation && this.noLocations) {
        this.isSearchedWithNoLocations = true;
        return;
      }

      this.$emit('search', this.localSearchModel as SearchModel, this.preSearchResultCount);
    },
    onLocationsChanged(locations: GeoLocation[]) {
      this.isSearchedWithNoLocations = locations.length === 0;
      this.locationsChanged(locations);
    },
    onResetSearch(searchModel?: SearchModel) {
      this.resetSearch(searchModel);
      trackResetFiltersClicked();
    },
    resetSearch(searchModel?: SearchModel) {
      this.setLocalSearchModel(
        searchModel ??
          new SearchModel()
            .withChooseType(this.localSearchModel.chooseType)
            .withLocations(this.localSearchModel.locations)
            .withRadius(this.localSearchModel.radius)
            .withListers(this.localSearchModel.listers),
      );

      this.clearFacilityCheckboxesModels();
    },
    searchLocations(searchText: string) {
      this.$store.dispatch('geo/autocompleteLocations', {
        language: this.i18n.locale.value,
        query: searchText,
      });
    },
    clearFacilityCheckboxesModels() {
      (Object.keys(this.facilities) as Facility[]).forEach((item) => {
        this.facilities[item] = false;
      });
    },
    onPriceChange(
      priceCriteria: NumberRange & { priceType?: PriceTypeOptions; isPriceDefined?: boolean },
    ) {
      const { priceType, isPriceDefined, ...priceRange } = priceCriteria;

      if (priceType && priceType !== this.priceType) {
        this.priceType = priceType;
      }

      this.localPriceRange = { ...this.localPriceRange, ...priceRange };

      if (this.priceType === PriceTypeOptions.ABSOLUTE) {
        this.setPrices(priceRange);
        this.setYearlyRentPerSqm({});
      } else {
        this.setYearlyRentPerSqm(priceRange);
        this.setPrices({});
      }

      this.setIsPriceDefined(isPriceDefined);
    },
    chooseTypeChanged(chooseType: ChooseType) {
      this.resetSearch(
        new SearchModel()
          .withChooseType(chooseType)
          .withLocations(this.localSearchModel.locations)
          .withRadius(this.localSearchModel.radius)
          .withListers(this.localSearchModel.listers),
      );
    },
    freeTextFieldChanged(query: string): void {
      const textSearch = query ? { query, language: this.i18n.locale.value } : undefined;
      this.$store.commit('searchBar/textFieldChanged', textSearch);
    },
    saveTextFieldValidationState(validationState: ValidationStates): void {
      this.freeTextFieldValidationState = validationState;
    },
    trackExtendedFilters(isOpen: boolean): void {
      if (isOpen) {
        trackOpenExtendedFilter();
      }
    },
    ...mapMutations({
      setLocalSearchModel: 'searchBar/setSearchModel',
      setRooms: 'searchBar/setRooms',
      setPrices: 'searchBar/setPrices',
      setYearlyRentPerSqm: 'searchBar/setYearlyRentPerSqm',
      locationsChanged: 'searchBar/locationsChanged',
      objectTypesChanged: 'searchBar/objectTypesChanged',
      radiusChanged: 'searchBar/radiusChanged',
      offerTypeChanged: 'searchBar/offerTypeChanged',
      floorTypeChanged: 'searchBar/floorTypeChanged',
      surfaceLivingRangeChanged: 'searchBar/surfaceLivingRangeChanged',
      surfaceUsableRangeChanged: 'searchBar/surfaceUsableRangeChanged',
      surfacePropertyRangeChanged: 'searchBar/surfacePropertyRangeChanged',
      buildYearRangeChanged: 'searchBar/buildYearRangeChanged',
      volumeRangeChanged: 'searchBar/volumeRangeChanged',
      availabilityChanged: 'searchBar/availabilityChanged',
      setIsPriceDefined: 'searchBar/setIsPriceDefined',
    }),
  },
});
</script>
<style lang="scss" scoped>
@import '@/assets/variables';

:deep(.filtersButtonMobile button) {
  height: $control-bar-mobile-height;
}
</style>
